import { AcxChipPropsIcon } from "components/UI/AcxChip";
import { ConversationClassifier } from "services/ConversationService";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";

// string constants for classifier names
export const ML_SE_Acknowledgement = "ML-SE-Acknowledgement";
export const ML_AdverseEvents = "ML-AdverseEvents";
export const ML_AdverseEvents_P2 = "ML-AdverseEvents-P2";
export const ML_SE_Identification = "ML-SE-Identification";
export const ML_EddyEffect_P2 = "ML-EddyEffect-P2";
export const ML_Sentiment = "ML-Sentiment";
export const ML_SentimentV2 = "ML-SentimentV2";
export const ML_PatientJourney = "ML-PatientJourney";
export const ML_Hipaa = "ML-Hipaa";
export const ML_Topic_Cluster = "ML-Topic-Cluster";
export const ML_Topic_LLM = "ML-Topic-LLM";
export const ML_CallDrivers = "ML-CallDrivers";
export const ML_SpeakerIdentification = "ML-SpeakerIdentification";
export const ML_ContactType = "ML-ContactType";
export const ML_PQC = "ML-PQC";
export const ML_SpecialSituation = "ML-SpecialSituation";
export const ML_EddyEffect = "ML-EddyEffect";

export const safetyEventTags = [
    ML_AdverseEvents_P2,
    ML_AdverseEvents,
    ML_SE_Identification,
    ML_SE_Acknowledgement,
    ML_SpecialSituation,
    ML_EddyEffect_P2,
    ML_Sentiment,
];

const safetyEventChildTags = [
    ML_AdverseEvents,
    ML_AdverseEvents_P2,
    ML_PQC,
    ML_SpecialSituation,
    ML_SE_Acknowledgement,
];

export function isFalseClassifierResult(classifier: ConversationClassifier): boolean {
    if (classifier.result === "false") return true;
    if (!safetyEventChildTags.includes(classifier.name)) return false;

    if (classifier.result === "No SE Acknowledgement") return true;
    try {
        const label = JSON.parse(classifier.result)?.Label;
        return label === "false" || label === "No SE Acknowledgement";
    } catch {
        return false;
    }
}

export const hiddenTags = [
    ML_EddyEffect_P2,
    ML_Sentiment,
    ML_AdverseEvents_P2,
    ML_Topic_LLM,
    ML_CallDrivers,
    ML_SpeakerIdentification,
];

export function getChipOptionsByClassifier(
    classifier: ConversationClassifier,
    store: ApplicationFilterValuesStore,
) {
    let color = "lightGray";
    let label: string | React.ReactNode = classifier.name;
    let icon: AcxChipPropsIcon | undefined;

    // parse only if valid JSON
    const parseResult = (result) => {
        if (typeof result === "string") {
            if (checkForJson(result)) {
                try {
                    return JSON.parse(result);
                } catch (parseError) {
                    console.error("Error parsing JSON:", parseError);
                    return null;
                }
            }
            return { Label: result };
        }
        return result;
    };

    const checkForJson = (str) => {
        return (
            (str.startsWith("{") && str.endsWith("}")) ||
            (str.startsWith("[") && str.endsWith("]"))
        );
    };

    const result = parseResult(classifier.result);

    if (!result) {
        console.log("No valid result found.");
        return { label, color, icon };
    }

    if (classifier.classifierType === "Tensorflow") {
        switch (classifier.name) {
            case ML_EddyEffect:
                label = "Eddy Effect Signal";
                color = "eddy";
                icon = "eddy";
                break;
            case ML_AdverseEvents:
                label = "Adverse Event";
                color = "adverse";
                break;
            case ML_AdverseEvents_P2:
                // this is hidden and possibly completely unused now
                label = "Adverse Event";
                color = "adverse";
                break;
            case ML_SE_Acknowledgement:
                if (
                    result.Label === "SE Acknowledgement" ||
                    result.Label === "true"
                ) {
                    label = "Safety Event Acknowledged";
                    color = "green";
                } else if (
                    result.Label === "No SE Acknowledgement" ||
                    result.Label === "false"
                ) {
                    label = "Safety Event Not Acknowledged";
                    color = "yellow";
                }
                break;
            case ML_SE_Identification:
                label = "Safety Event Identified";
                color = "yellow";
                break;
            case ML_PQC:
                label = "Product Quality Complaint";
                color = "yellow";
                break;
            case ML_SpecialSituation:
                label = "Special Situation";
                color = "yellow";
                break;
            case ML_Hipaa:
                if (result.Label === "Compliant") {
                    label = "HIPAA Compliant";
                    color = "darkGreen";
                } else if (result.Label === "Not Compliant") {
                    label = "Not HIPAA Compliant";
                    color = "red";
                } else {
                    label = "HIPAA N/A";
                    color = "gray";
                }
                break;
            case ML_ContactType:
                label = store.getContactTypeLabel(result.Label);
                color = "contact";
                break;
            case ML_PatientJourney:
                label = result.Label;
                color = "patientJourney";
                break;
            default:
                color = "lightGray";
        }
    }

    if (classifier.name === ML_Topic_Cluster) {
        color = "darkGray";

        label = result.TopicCluster1 ?? "";
    }

    let label2: string | undefined;
    let color2: string | undefined;

    if (classifier.name === ML_SentimentV2) {
        label = `Sentiment Start: ${result.starting}`;
        color =
            result.starting === "Positive"
                ? "green"
                : result.starting === "Neutral"
                ? "yellow"
                : "red";
        label2 = `Sentiment End: ${result.ending}`;
        color2 =
            result.ending === "Positive"
                ? "green"
                : result.ending === "Neutral"
                ? "yellow"
                : "red";
    }

    return { label, color, icon, label2, color2 };
}

/**
 * Used to determine how many chips should be shown vs in the overflow tooltip
 * @returns the number of classifiers to keep in the list, the rest will be in the overflow.
 */
export function getSliceCountForTooltipCutoff(
    classifierList,
    hideTopicCluster,
) {
    let sliceCount = 9;
    let sliceAdjustment = 0;
    if (
        !!classifierList.filter((item) => item.name === ML_SentimentV2).length
    ) {
        // Change slice count when sentimentV2 is present because it creates 2 chips with very long labels
        sliceAdjustment += 3;
    }

    if (!hideTopicCluster) {
        sliceAdjustment += 1;
    }

    sliceCount = sliceCount - sliceAdjustment;
    return sliceCount;
}
